import React, { useState, useEffect, useCallback, useMemo } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import * as style from "./ValueStreams.module.scss";
import Card from "./Card";
import ValueStreamSidePanel from "../../pages/ValueStreams/ValueStreamSidePanel";
import { LoadingSpinner, Search } from "cprime-foundational-components";
import ValueStreamsPanelContent from "./ValueStreamsPanelContent";
import { triggerToast } from "../../utils/toast";
import {
  getValueStreamsData,
  deleteValueStream,
} from "../../services/RequestAccess/RequestAccess";
import { Box } from "@mui/material";

import Button from "../../Components/Common/CprimeButton";
interface Feature {
  id: number;
  featureName: string;
  featureDescription: string | null;
  isChecked?: boolean;
}
interface ValueStreamProps {
  isCreateValueStream?: boolean;
  setCreateModal?: any;
  getData?: any;
}

interface ValueStream {
  groupId: number;
  id: number;
  name: string;
  description: string;
  groupDescription: string;
  features: Feature[];
  group: any;
}

interface ExpandedState {
  [key: number]: boolean;
}

const ValueStreams: React.FC<ValueStreamProps> = ({
  isCreateValueStream = false,
  setCreateModal = () => null,
}) => {
  const scssClass = style as any;

  const [data, setData] = useState<ValueStream[]>([]);
  const [expandedCards, setExpandedCards] = useState<ExpandedState>({});
  const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
  const [searchValue, setSearchValue] = useState<string>("");
  const [isGroupSidePanelVisible, setIsGroupSidePanelVisible] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeSelect, setActiveSelect] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const fetchValueStreamsData = async () => {
    setIsLoading(true);
    try {
      setIsLoading(true);
      const valueStreamsData = await getValueStreamsData();
      setData(valueStreamsData?.data);
    } catch (error) {
      triggerToast("Error fetching permissions", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: any, handleClose: Function) => {
    setIsLoading(true);
    try {
      const payload = {
        value_stream_id: id,
      };
      const response = await deleteValueStream(payload);
      const refreshedValueStreams = await getValueStreamsData();
      setData(refreshedValueStreams?.data ?? []);
      handleClose(false);
      if (response?.error) {
        triggerToast(`${response?.error}`, "error");
      } else if (response?.status === true) {
        triggerToast(`${response?.message}`, "success");
      }
      triggerToast(`${response?.message}`, response?.status?.toLowerCase());
    } catch (err) {
      // console.error("Group save failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchValue = useCallback((query: string) => {
    setSearchValue(query);
  }, []);

  useEffect(() => {
    fetchValueStreamsData();
  }, []);

  useEffect(() => {
    if (isCreateValueStream) {
      setIsGroupSidePanelVisible(true);
    }
  }, [isCreateValueStream]);

  const rows = useMemo(() => {
    const normalizedSearchValue = searchValue.toLowerCase();
    const displayData =
      searchValue.trim() === ""
        ? data
        : data?.filter((feature) =>
            feature?.name?.toLowerCase()?.includes(normalizedSearchValue),
          );

    const rows: ValueStream[][] = [];
    if (displayData?.length) {
      for (let i = 0; i < displayData?.length; i += 4) {
        rows?.push(displayData?.slice(i, i + 4));
      }
    }

    return rows;
  }, [data, searchValue]);

  const toggleRow = (rowIndex: number): void => {
    const shouldExpand = !expandedRows[rowIndex];

    setExpandedRows((prev) => ({
      ...prev,
      [rowIndex]: shouldExpand,
    }));

    const rowCards = rows[rowIndex];
    const newExpandedCards = { ...expandedCards };

    rowCards.forEach((card) => {
      newExpandedCards[card?.id] = shouldExpand;
    });

    setExpandedCards(newExpandedCards);
  };

  const toggleAllCards = (expand: boolean): void => {
    const newExpandedCards: ExpandedState = {};
    data.forEach((group) => {
      newExpandedCards[group?.id] = expand;
    });
    setExpandedCards(newExpandedCards);

    const newExpandedRows: ExpandedState = {};
    rows.forEach((_, index) => {
      newExpandedRows[index] = expand;
    });
    setExpandedRows(newExpandedRows);
  };

  const getExpansion = (value: boolean) => {
    value
      ? setExpandedCards(
          data?.reduce((acc, grp) => {
            acc[grp?.id] = true;
            return acc;
          }, {} as ExpandedState),
        )
      : setExpandedCards({});
  };

  const [hasInitialized, setHasInitialized] = useState(false);

  const handleSearchChange = (e: string) => {
    setSearchValue?.(e);
    handleSearchValue(e);
  };

  useEffect(() => {
    if (!hasInitialized && data?.length > 0) {
      toggleAllCards(true);
      setHasInitialized(true);
    }
  }, [data, hasInitialized]);

  const handleExpandClick = () => {
    setActiveSelect("Expand Section");
    getExpansion(true);
  };

  const handleCollapseClick = () => {
    setActiveSelect("Collapse Section");
    getExpansion(false);
  };

  const getData = (name: string, description: string) => {
    return { name, description };
  };

  return (
    <div className={scssClass.ValueStreamsContainer}>
      <div className={scssClass.ValueStreamsContainerHader}>
        <div className={scssClass.container}>
          <div className={scssClass.header}>
            <div className={scssClass.title}>Value Streams</div>
            <Button
              variant="tertiary"
              size="medium"
              onClick={() => setIsGroupSidePanelVisible(true)}
            >
              Create Value Stream
            </Button>
          </div>
        </div>

        <div className={scssClass.container}>
          <div className={scssClass.header}>
            <Search
              autoFocus={false}
              value={searchValue || ""}
              placeholder="Search"
              onChange={(e) => handleSearchChange(e)}
              onSubmit={() => {}}
              onClear={() => handleSearchChange("")}
              className={scssClass.search}
              sx={{
                width: "333px",
                ".MuiButtonBase-root.MuiIconButton-root": {
                  padding: "0.5rem 0.75rem 0.5rem 0rem",
                  fontSize: "1.5rem",
                },
              }}
              onSearch={function (value: string): void {}}
            />
            <div className={scssClass.clickSection}>
              <span
                role="button"
                onKeyDown={() => {}}
                data-testid={"expand-section"}
                tabIndex={0}
                className={`expand ${
                  activeSelect === "Expand Section" ? "active" : ""
                }`}
                onClick={handleExpandClick}
              >
                Expand Section
              </span>
              <span className={scssClass.separator}>|</span>
              <span
                role="button"
                tabIndex={0}
                className={`collapse ${
                  activeSelect === "Collapse Section" ? "active" : ""
                }`}
                onClick={handleCollapseClick}
                onKeyDown={() => {}}
              >
                Collapse Section
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        className={scssClass.sectionContainer}
        style={{ scrollbarWidth: "none" }}
      >
        <div className={scssClass.featureGroupContainer}>
          <span>Value Streams</span>
        </div>
        {isLoading ? (
          <Box
            sx={{
              textAlign: "center",
              padding: "24px",
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            <LoadingSpinner />
          </Box>
        ) : (
          <div className={scssClass.containermain}>
            <div
              className={scssClass.cardGridContainer}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
              }}
            >
              {rows?.map((data, rowIndex) => (
                <div key={rowIndex} className={scssClass.rowMap}>
                  <div className={scssClass.carddiv}>
                    {data?.map((item) => {
                      return (
                        <Card
                          key={item?.id ?? item?.name}
                          name={item?.name}
                          description={item?.description}
                          id={item?.id}
                          group={item}
                          expandedCards={expandedCards}
                          handleDelete={handleDelete}
                        />
                      );
                    })}
                  </div>
                  <button
                    onClick={() => toggleRow(rowIndex)}
                    className={scssClass.buttonRowExpandCollapse}
                    data-testid={"expand"}
                  >
                    {expandedRows[rowIndex] ? (
                      <KeyboardArrowUpIcon
                        className={scssClass.chevronUpDown}
                      />
                    ) : (
                      <KeyboardArrowDownIcon
                        className={scssClass.chevronUpDown}
                      />
                    )}
                  </button>
                </div>
              ))}
              {searchValue?.trim() !== "" && rows?.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No matching value streams found.
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {(isCreateValueStream || isGroupSidePanelVisible) && (
        <ValueStreamSidePanel
          open={isCreateValueStream || isGroupSidePanelVisible}
          nameValueStream={name}
          setNameValueStream={setName}
          setDes={setDescription}
          descriptionValueStream={description}
          refreshData={setData}
          onClose={() => {
            if (isCreateValueStream) {
              setCreateModal(false);
              setIsGroupSidePanelVisible(false);
            } else setIsGroupSidePanelVisible(false);
          }}
          BodyComponent={
            <ValueStreamsPanelContent
              nameValueStream={setName}
              descriptionValueStream={setDescription}
              getData={getData}
            />
          }
        />
      )}
    </div>
  );
};

export default ValueStreams;
